// The widgets that reach outside: the weather (Open-Meteo), the house (Home
// Assistant's states, a tap toggles), lyrics that follow the player (LRCLIB)
// and Spotify's queue. They were the desk's own cards until it folded into
// Home (2026-10-10). Every one is online-only behind a master-only endpoint
// (internal/desk), so each keeps its last answer in memory and paints it at
// once when it comes back, shows a Skeleton only on its very first ask, and
// asks again only while the page is visible. What they need to know (a place,
// the entities) is `widgets` on the Settings record, chosen in Settings →
// Home screen (os/WidgetSettings.tsx).
import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  Blinds,
  CircleDot,
  DoorClosed,
  DoorOpen,
  Droplets,
  Fan,
  Gauge,
  Heater,
  Lightbulb,
  LightbulbOff,
  Lock,
  LockOpen,
  Power,
  Thermometer,
  Tv,
  User,
} from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { api, db } from '@/runtime'
import type { HomeEntity, Lyrics, QueueItem, Weather } from '@/db/types'
import { useSettings } from '@/db/hooks'
import { mySettingsId, patchSettings } from '@/lib/settingsWrite'
import { useNow } from '@/hooks/useNow'
import { usePlayer } from '@/state/player'
import { Skeleton } from '@/components/Skeleton'
import { Failed, cx } from '@/components/ui'
import type { WidgetSize } from './layout'
import { Frame, Quiet } from './widgets'
import { legacyDesk, widgetConfig, widgetPatch, type WidgetConfig } from './widgetConfig'

// ---- config -------------------------------------------------------------------

// what the desk kept on this device, read once: a fallback until Settings has its own
const LEGACY = legacyDesk()

/** The widgets' config: the synced one, with this device's old desk choices under it. */
export const useWidgetConfig = (): WidgetConfig => widgetConfig(useSettings().widgets, LEGACY)

/** Change the widgets' config: one Settings patch, so it is undoable like any write. */
export const saveWidgetConfig = async (change: Partial<WidgetConfig>) => {
  const r = await db.resources.get(mySettingsId())
  const current = widgetConfig((r?.spec as { widgets?: unknown } | undefined)?.widgets, LEGACY)
  const widgets = widgetPatch(current, change)
  await patchSettings({ widgets })
}

// ---- asking -------------------------------------------------------------------

const answers = new Map<string, unknown>()

/**
 * The last answer for `key`, asked for at once when there is none held and
 * then every `every` ms while the page is visible (0: once per key). `reload`
 * asks now. A null key asks nothing.
 */
const useAsk = <T,>(key: string | null, ask: (key: string) => Promise<T>, every: number) => {
  // failed: why the last ask failed, until one answers
  const [held, setHeld] = useState<{ key: string | null; data?: T; failed: string | null }>(() => ({
    key,
    data: key ? (answers.get(key) as T | undefined) : undefined,
    failed: null,
  }))
  const [nudge, setNudge] = useState(0)
  useEffect(() => {
    if (!key) return
    let live = true
    const load = () => {
      if (document.visibilityState === 'hidden') return
      ask(key)
        .then((data) => {
          answers.set(key, data)
          if (live) setHeld({ key, data, failed: null })
        })
        .catch((e: unknown) => {
          const why = e instanceof Error ? e.message : 'no answer'
          if (live) setHeld((h) => ({ key, data: h.key === key ? h.data : undefined, failed: why }))
        })
    }
    load()
    if (!every)
      return () => {
        live = false
      }
    const t = window.setInterval(load, every)
    return () => {
      live = false
      window.clearInterval(t)
    }
  }, [key, ask, every, nudge])
  const mine = held.key === key
  // a key asked before paints its last answer at once, as one coming back does
  const data = mine ? held.data : key ? (answers.get(key) as T | undefined) : undefined
  return { data, failed: mine ? held.failed : null, reload: () => setNudge((n) => n + 1) }
}

const Loading = ({ lines = 2 }: { lines?: number }) => (
  <div className="flex-1 flex flex-col justify-end gap-2" aria-hidden>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} className={cx('h-5', i === 0 ? 'w-24' : 'w-full')} />
    ))}
  </div>
)

/** A widget whose read failed says so in its own frame, with Try again. */
const Offline = ({ what, why, retry }: { what: string; why: string | null; retry: () => void }) => (
  <Failed compact what={what} reason={why} onRetry={retry} />
)

/** Where the widgets' choices are made. */
const SetUp = ({ children }: { children: string }) => {
  const navigate = useNavigate()
  return (
    <div className="flex-1 flex flex-col items-start justify-center gap-2">
      <p className="text-[13px] text-faint">{children}</p>
      <button
        type="button"
        onClick={() => void navigate({ to: '/settings/$section', params: { section: 'home' } })}
        className="rounded-full bg-raised border border-line px-3 min-h-8 text-xs"
      >
        Choose in Settings
      </button>
    </div>
  )
}

// ---- weather ------------------------------------------------------------------

/** A glyph for a WMO weather code. */
export const weatherGlyph = (code: number, day = true) => {
  if (code === 0) return day ? '☀️' : '🌙'
  if (code <= 2) return day ? '🌤️' : '☁️'
  if (code === 3) return '☁️'
  if (code <= 48) return '🌫️'
  if (code <= 57) return '🌦️'
  if (code <= 67) return '🌧️'
  if (code <= 77) return '🌨️'
  if (code <= 82) return '🌧️'
  if (code <= 86) return '🌨️'
  return '⛈️'
}

const deg = (n: number) => `${Math.round(n)}°`
const dayName = (date: string, i: number) =>
  i === 0 ? 'Today' : new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' })

const askWeather = (key: string) => {
  const [lat, lon, units] = key.split(',')
  return api.weather(Number(lat), Number(lon), units)
}

export const WeatherWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const { place, units } = useWidgetConfig()
  const key = place ? `${place.latitude},${place.longitude},${units}` : null
  const { data: w, failed, reload } = useAsk<Weather>(key, askWeather, 10 * 60_000)
  // the town, not the county and country after it
  const title = place?.name.split(',')[0] || 'Weather'
  const today = w?.days[0]
  return (
    <Frame kind="weather" title={title} editing={editing}>
      {!place ? (
        <SetUp>No place chosen yet.</SetUp>
      ) : !w ? (
        failed ? (
          <Offline what="the weather" why={failed} retry={reload} />
        ) : (
          <Loading />
        )
      ) : (
        <div
          className={cx('flex-1 min-h-0 flex gap-3', size === 'small' ? 'flex-col justify-end' : 'items-end')}
        >
          <div className="min-w-0 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[30px] leading-none" aria-hidden>
                {weatherGlyph(w.code, w.is_day)}
              </span>
              <span className="text-[32px] font-bold leading-none tracking-tight">{deg(w.temperature)}</span>
            </div>
            <div className="text-[13px] text-fg-2 truncate mt-1.5">{w.text}</div>
            <div className="text-[11px] text-faint truncate">
              {size === 'small' && today
                ? `High ${deg(today.high)}, low ${deg(today.low)}`
                : `Feels ${deg(w.feels_like)}, wind ${Math.round(w.wind)} mph`}
            </div>
          </div>
          {size !== 'small' && (
            <ul className="flex-1 min-w-0 grid grid-cols-4 gap-1" data-testid="widget-weather-days">
              {w.days.slice(0, 4).map((d, i) => (
                <li key={d.date} className="flex flex-col items-center gap-0.5 min-w-0">
                  <span className="text-[11px] text-faint truncate">{dayName(d.date, i)}</span>
                  <span className="text-lg leading-none" aria-hidden>
                    {weatherGlyph(d.code, true)}
                  </span>
                  <span className="text-xs">
                    {deg(d.high)} <span className="text-faint">{deg(d.low)}</span>
                  </span>
                  {/* the chance of rain, said by its droplet, not a bare percentage */}
                  {d.rain_pct >= 30 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] text-accent-text"
                      aria-label={`${d.rain_pct}% chance of rain`}
                      title={`${d.rain_pct}% chance of rain`}
                    >
                      <Droplets size={10} aria-hidden />
                      {d.rain_pct}%
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Frame>
  )
}

// ---- the house ----------------------------------------------------------------

/** A line icon for an entity, drawn like every other widget's (no emoji). */
export const EntityIcon = ({
  e,
  size = 18,
  className,
}: {
  e: HomeEntity
  size?: number
  className?: string
}) => {
  const p = { size, className, 'aria-hidden': true }
  const temp = e.unit === '°C' || e.unit === '°F'
  switch (e.domain) {
    case 'light':
      return e.state === 'on' ? <Lightbulb {...p} /> : <LightbulbOff {...p} />
    case 'switch':
    case 'input_boolean':
      return <Power {...p} />
    case 'fan':
      return <Fan {...p} />
    case 'climate':
      return <Heater {...p} />
    case 'lock':
      return e.state === 'locked' ? <Lock {...p} /> : <LockOpen {...p} />
    case 'cover':
      return <Blinds {...p} />
    case 'media_player':
      return <Tv {...p} />
    case 'binary_sensor':
      return doorLike(e) ? (
        e.state === 'on' ? (
          <DoorOpen {...p} />
        ) : (
          <DoorClosed {...p} />
        )
      ) : (
        <Activity {...p} />
      )
    case 'sensor':
      return temp ? <Thermometer {...p} /> : e.unit === '%' ? <Droplets {...p} /> : <Gauge {...p} />
    case 'person':
      return <User {...p} />
    default:
      return <CircleDot {...p} />
  }
}

const doorLike = (e: HomeEntity) =>
  ['door', 'window', 'opening', 'garage_door'].includes(String(e.attrs?.device_class ?? '')) ||
  /door|window/i.test(e.id)

/** What an entity is, in a word, for telling two of one name apart ("Living room light", "Living room temperature"). */
const entityNoun = (e: HomeEntity) => {
  if (e.domain === 'sensor')
    return e.unit === '°C' || e.unit === '°F' ? 'temperature' : e.unit === '%' ? 'humidity' : 'sensor'
  if (e.domain === 'climate') return 'heating'
  if (e.domain === 'binary_sensor') return doorLike(e) ? 'door' : 'sensor'
  if (e.domain === 'input_boolean') return 'switch'
  if (e.domain === 'media_player') return 'player'
  return e.domain.replace(/_/g, ' ')
}

/** Names as the widget shows them: an entity sharing its name with another says what it is. */
export const entityNames = (shown: HomeEntity[]): Map<string, string> => {
  const seen = new Map<string, number>()
  for (const e of shown) seen.set(e.name.toLowerCase(), (seen.get(e.name.toLowerCase()) ?? 0) + 1)
  return new Map(
    shown.map((e) => {
      const noun = entityNoun(e)
      const twice = (seen.get(e.name.toLowerCase()) ?? 0) > 1 && !e.name.toLowerCase().includes(noun)
      return [e.id, twice ? `${e.name} ${noun}` : e.name]
    }),
  )
}

const deg1 = (v: string | number) => {
  const n = Number(v)
  return Number.isFinite(n) ? `${Math.round(n * 10) / 10}°` : `${v}`
}

const say = (state: string) => (state ? state[0]!.toUpperCase() + state.slice(1).replace(/_/g, ' ') : state)

/** The state in words: temperatures in one unit, locks and doors as people say them, the rest capitalised. */
export const stateText = (e: HomeEntity) => {
  const temp = e.unit === '°C' || e.unit === '°F'
  if (e.domain === 'climate') {
    const t = e.attrs?.current_temperature
    return typeof t === 'number' ? deg1(t) : say(e.state)
  }
  if (e.domain === 'sensor') return temp ? deg1(e.state) : `${e.state}${e.unit ?? ''}`
  if (e.domain === 'lock')
    return e.state === 'locked' ? 'Locked' : e.state === 'unlocked' ? 'Unlocked' : say(e.state)
  if (e.domain === 'binary_sensor')
    return doorLike(e) ? (e.state === 'on' ? 'Open' : 'Closed') : e.state === 'on' ? 'Detected' : 'Clear'
  if (e.state === 'on' && typeof e.attrs?.brightness === 'number')
    return `${Math.round((e.attrs.brightness / 255) * 100)}%`
  return say(e.state)
}

const isLit = (e: HomeEntity) =>
  e.toggle && (e.state === 'on' || e.state === 'open' || e.state === 'unlocked' || e.state === 'playing')

/** The service a tap calls: locks lock and unlock, covers open and close, the rest toggle. */
const serviceFor = (e: HomeEntity) =>
  e.domain === 'lock'
    ? e.state === 'locked'
      ? 'unlock'
      : 'lock'
    : e.domain === 'cover'
      ? e.state === 'open'
        ? 'close_cover'
        : 'open_cover'
      : 'toggle'

const askHouse = (key: string) => api.homeStates(key.split(','))

export const HouseWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const { house } = useWidgetConfig()
  const key = house.length ? house.join(',') : null
  const { data, failed, reload } = useAsk(key, askHouse, 5000)
  const [busy, setBusy] = useState<string | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const toggle = async (e: HomeEntity) => {
    if (!e.toggle || busy) return
    setBusy(e.id)
    setRefused(null)
    try {
      await api.homeCall(e.id, serviceFor(e))
      reload()
    } catch (err) {
      setRefused(err instanceof Error ? err.message : 'Home Assistant refused')
    } finally {
      setBusy(null)
    }
  }
  const shown = (data?.entities ?? []).slice(0, size === 'large' ? 10 : 4)
  const names = entityNames(shown)
  return (
    <Frame
      kind="house"
      editing={editing}
      right={
        refused ? <span className="text-[11px] text-faint truncate max-w-40">{refused}</span> : undefined
      }
    >
      {!key ? (
        <SetUp>Nothing from Home Assistant chosen yet.</SetUp>
      ) : !data ? (
        failed ? (
          <Offline what="the house" why={failed} retry={reload} />
        ) : (
          <Loading />
        )
      ) : !data.configured ? (
        <Quiet>Home Assistant is not set up on the server.</Quiet>
      ) : (
        <ul
          className="grid grid-cols-2 gap-1.5 min-h-0 overflow-y-auto content-start"
          data-testid="widget-house"
        >
          {shown.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => void toggle(e)}
                disabled={!e.toggle}
                aria-pressed={e.toggle ? isLit(e) : undefined}
                className={cx(
                  'w-full text-left rounded-[max(3px,calc(var(--radius-card)-12px))] px-2.5 min-h-[50px] flex items-center gap-2 transition-colors',
                  isLit(e) ? 'bg-accent text-on-accent' : 'bg-raised',
                  e.toggle && 'active:scale-[0.98]',
                  busy === e.id && 'opacity-60',
                )}
              >
                <EntityIcon e={e} className={cx('shrink-0', isLit(e) ? '' : 'text-muted')} />
                <span className="min-w-0">
                  <span className="block text-[13px] leading-4 truncate">{names.get(e.id) ?? e.name}</span>
                  <span className={cx('block text-[11px] truncate', isLit(e) ? 'opacity-80' : 'text-faint')}>
                    {stateText(e)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Frame>
  )
}

// ---- lyrics -------------------------------------------------------------------

const askLyrics = (key: string) => {
  const [artist, title, album, seconds] = JSON.parse(key) as [string, string, string, number]
  return api
    .lyrics(artist, title, album, seconds)
    .catch((): Lyrics => ({ found: false, synced: false, lines: [] }))
}

/**
 * Synced lyrics that follow the track, plain text when that is all there is.
 * The line is found from the player's progress run forward on the local
 * clock; the player store is the one poll (this only watches it).
 */
export const LyricsWidget = ({ size, editing }: { size: WidgetSize; editing: boolean }) => {
  const st = usePlayer((s) => s.state)
  const watch = usePlayer((s) => s.watch)
  useEffect(() => watch(), [watch])
  const now = useNow(500)
  const key =
    st?.connected && st.title
      ? JSON.stringify([st.artist ?? '', st.title, st.album ?? '', Math.round((st.duration_ms ?? 0) / 1000)])
      : null
  const { data: l, failed, reload } = useAsk(key, askLyrics, 0)
  const posSec = st
    ? (st.progress_ms + (st.playing ? Math.max(0, now - Date.parse(st.fetched_at)) : 0)) / 1000
    : 0
  let active = -1
  if (l?.synced) for (let i = 0; i < l.lines.length; i++) if (l.lines[i]!.at <= posSec) active = i
  const list = useRef<HTMLUListElement>(null)
  useEffect(() => {
    // scroll the list itself, never scrollIntoView: that would drag Home's pages sideways
    const el = list.current
    const line = el?.querySelector<HTMLElement>('[data-active="true"]')
    if (!el || !line) return
    el.scrollTo({ top: line.offsetTop - el.clientHeight / 2 + line.offsetHeight / 2, behavior: 'smooth' })
  }, [active])
  return (
    <Frame
      kind="lyrics"
      editing={editing}
      right={
        st?.title ? <span className="text-[11px] text-faint truncate max-w-48">{st.title}</span> : undefined
      }
    >
      {!st?.connected ? (
        <Quiet>Spotify is not connected.</Quiet>
      ) : !st.title ? (
        <Quiet>Nothing playing.</Quiet>
      ) : !l ? (
        failed ? (
          <Offline what="the lyrics" why={failed} retry={reload} />
        ) : (
          <Loading lines={size === 'large' ? 4 : 2} />
        )
      ) : !l.found ? (
        <Quiet>No lyrics for this one.</Quiet>
      ) : l.synced ? (
        <ul
          ref={list}
          className="relative flex-1 min-h-0 overflow-y-auto scrollbar-none flex flex-col gap-1 [mask-image:linear-gradient(transparent,black_18%,black_82%,transparent)]"
          data-testid="widget-lyrics"
        >
          {l.lines.map((line, i) => (
            <li
              key={`${line.at}-${i}`}
              data-active={i === active || undefined}
              className={cx(
                'leading-snug transition-colors duration-300',
                size === 'large' ? 'text-[17px]' : 'text-[15px]',
                i === active ? 'text-fg font-semibold' : i < active ? 'text-faint' : 'text-muted',
              )}
            >
              {line.text || '…'}
            </li>
          ))}
        </ul>
      ) : (
        <pre className="flex-1 min-h-0 overflow-y-auto scrollbar-none whitespace-pre-wrap font-[inherit] text-[13px] text-fg-2">
          {l.plain}
        </pre>
      )}
    </Frame>
  )
}

// ---- up next ------------------------------------------------------------------

const mmss = (ms: number) =>
  `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`

const askQueue = () => api.spotifyQueue()

/** Spotify's queue: asked again when the track changes, and every half minute while shown. */
export const UpNextWidget = ({ editing }: { editing: boolean }) => {
  const st = usePlayer((s) => s.state)
  const watch = usePlayer((s) => s.watch)
  useEffect(() => watch(), [watch])
  const key = st?.connected ? `queue:${st.title ?? ''}:${st.artist ?? ''}` : null
  const { data, failed, reload } = useAsk<{ connected: boolean; queue: QueueItem[] }>(key, askQueue, 30_000)
  const queue = (data?.queue ?? []).slice(0, 3)
  return (
    <Frame kind="upnext" editing={editing}>
      {!st?.connected ? (
        <Quiet>Spotify is not connected.</Quiet>
      ) : !data ? (
        failed ? (
          <Offline what="what is up next" why={failed} retry={reload} />
        ) : (
          <Loading />
        )
      ) : queue.length === 0 ? (
        <Quiet>Nothing queued.</Quiet>
      ) : (
        <ul className="flex flex-col gap-1.5 min-h-0" data-testid="widget-upnext">
          {queue.map((q, i) => (
            <li key={`${q.uri}-${i}`} className="flex items-center gap-2.5 min-w-0">
              {q.image ? (
                <img
                  src={q.image}
                  alt=""
                  className="h-8 w-8 rounded-md object-cover shrink-0"
                  loading="lazy"
                />
              ) : (
                <span className="h-8 w-8 rounded-md bg-raised shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] leading-4 truncate">{q.title}</span>
                <span className="block text-[11px] text-faint truncate">{q.artist}</span>
              </span>
              <span className="text-[11px] text-faint tabular-nums shrink-0">{mmss(q.duration_ms)}</span>
            </li>
          ))}
        </ul>
      )}
    </Frame>
  )
}
